import { MOVEMENT_TYPE_LABELS, PRODUCT_UNITS, UNIT_LABELS, formatDateTime, formatILS, type MovementType, type ProductUnit } from '@takumi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Boxes, ClipboardCheck, Pencil, Plus, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/form';
import { Badge, Card, CardHeader, Dialog, DialogContent, EmptyState, PageLoader } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Product } from '@/lib/sales-types';
import { cn } from '@/lib/utils';
import { SuppliersCard, useSuppliers } from '@/features/operations/SuppliersCard';

interface Movement {
  id: string;
  productId: string;
  movementType: MovementType;
  quantity: number;
  note: string | null;
  occurredAt: string;
  cancelledAt: string | null;
  cancelledByName: string | null;
  /** Manual, not yet cancelled: can be fixed or cancelled here (order/sample movements can't). */
  editable: boolean;
  productName: string;
  sku: string;
  orderNumber: string | null;
  batchCode: string | null;
  userName: string | null;
}

export function ProductsPage() {
  const { can } = useAuth();
  const products = useQuery({ queryKey: ['products', 'all'], queryFn: () => api.get<Product[]>('/products', { all: 1 }) });
  const movements = useQuery({ queryKey: ['movements'], queryFn: () => api.get<Movement[]>('/inventory/movements') });
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const [moving, setMoving] = useState<Product | null>(null);
  const [counting, setCounting] = useState<Product | null>(null);
  const [fixing, setFixing] = useState<Movement | null>(null);
  const [cancelling, setCancelling] = useState<Movement | null>(null);
  if (!products.data) return <PageLoader />;
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="מוצרים ומלאי"
        subtitle="מחירון בסיס, מדרגות כמות ומלאי זמין. מחירים מיוחדים ללקוח מוגדרים בכרטיס הלקוח."
        actions={can('pricing.manage') && <Button onClick={() => setEditing('new')}><Plus /> מוצר חדש</Button>}
      />
      <Card className="mb-6 overflow-hidden">
        {products.data.length === 0 ? (
          <EmptyState icon={<Boxes />} title="אין מוצרים עדיין" description="הוסיפו את מוצרי Takumi Matcha עם המחירון העסקי" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-sunken/80 text-xs font-bold text-muted">
                <tr className="[&>th]:px-3 [&>th]:py-2.5 [&>th]:text-start">
                  <th className="ps-4">מוצר</th><th>מחירון (לפני מע״מ)</th><th>מדרגות כמות</th><th>מלאי</th><th>עלות</th><th />
                </tr>
              </thead>
              <tbody className="divide-y divide-line/70">
                {products.data.map((p) => {
                  const low = p.trackInventory && p.minStock != null && Number(p.onHand ?? 0) < p.minStock;
                  return (
                    <tr key={p.id} className={cn(!p.isActive && 'opacity-50')}>
                      <td className="py-2.5 ps-4 pe-3">
                        <div className="font-semibold">{p.name}</div>
                        <div className="ltr text-start text-xs text-muted">{p.sku} · {UNIT_LABELS[p.unit]}</div>
                      </td>
                      <td className="px-3 font-semibold tabular">{formatILS(p.listPrice)}</td>
                      <td className="px-3 text-xs text-ink-soft">{p.tiers.length ? p.tiers.map((t) => `${Number(t.minQty)}+ → ${formatILS(t.unitPrice)}`).join(' · ') : '—'}</td>
                      <td className="px-3">
                        {p.trackInventory ? (
                          <span className={cn('font-semibold tabular', low && 'text-overdue')}>
                            {Number(p.onHand ?? 0)} {UNIT_LABELS[p.unit]}
                            {low && <Badge variant="overdue" className="ms-2"><AlertTriangle className="size-3" /> מלאי נמוך</Badge>}
                          </span>
                        ) : <span className="text-muted">ללא מעקב</span>}
                      </td>
                      <td className="px-3 text-muted tabular">{p.cost != null ? formatILS(p.cost) : '—'}</td>
                      <td className="px-3 text-end">
                        {can('inventory.write') && p.trackInventory && <Button size="sm" variant="ghost" onClick={() => setMoving(p)}><Boxes /> תנועת מלאי</Button>}
                        {can('inventory.write') && p.trackInventory && <Button size="sm" variant="ghost" onClick={() => setCounting(p)}><ClipboardCheck /> ספירת מלאי</Button>}
                        {can('pricing.manage') && <Button size="icon-sm" variant="ghost" onClick={() => setEditing(p)} aria-label="עריכה"><Pencil /></Button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <SuppliersCard />
      <Card>
        <CardHeader title="תנועות מלאי אחרונות" icon={<Boxes />} />
        {movements.data?.length ? (
          <ul className="divide-y divide-line/70 text-sm">
            {movements.data.slice(0, 50).map((m) => (
              <li key={m.id} className={cn('flex flex-wrap items-center gap-2 px-4 py-2', m.cancelledAt && 'opacity-60')}>
                <span className="w-32 text-xs text-muted tabular">{formatDateTime(m.occurredAt)}</span>
                <Badge variant={m.cancelledAt ? 'neutral' : m.quantity > 0 ? 'matcha' : 'neutral'}>{MOVEMENT_TYPE_LABELS[m.movementType]}</Badge>
                <span className={cn('font-semibold', m.cancelledAt && 'line-through')}>{m.productName}</span>
                <span className="text-xs text-muted">{[m.orderNumber && `הזמנה ${m.orderNumber}`, m.batchCode && `אצווה ${m.batchCode}`, m.note, m.userName].filter(Boolean).join(' · ')}</span>
                {m.cancelledAt && <Badge variant="overdue">בוטל{m.cancelledByName ? ` · ${m.cancelledByName}` : ''}</Badge>}
                <span className={cn('ltr ms-auto font-semibold tabular', m.cancelledAt ? 'text-muted line-through' : m.quantity < 0 ? 'text-overdue' : 'text-success')}>{m.quantity > 0 ? '+' : ''}{m.quantity}</span>
                {can('inventory.write') && (
                  <span className="flex w-16 justify-end gap-0.5">
                    {m.editable && (
                      <>
                        <Button size="icon-sm" variant="ghost" onClick={() => setFixing(m)} aria-label="תיקון כמות" title="תיקון כמות"><Pencil /></Button>
                        <Button size="icon-sm" variant="ghost" onClick={() => setCancelling(m)} aria-label="ביטול תנועה" title="ביטול תנועה"><Undo2 /></Button>
                      </>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : <EmptyState title="אין תנועות מלאי" />}
      </Card>
      <ProductDialog product={editing === 'new' ? null : editing} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} />
      {moving && <MovementDialog product={moving} open onOpenChange={(o) => !o && setMoving(null)} />}
      {counting && <StockCountDialog product={counting} onClose={() => setCounting(null)} />}
      {fixing && <FixMovementDialog movement={fixing} onClose={() => setFixing(null)} />}
      {cancelling && <CancelMovementDialog movement={cancelling} onClose={() => setCancelling(null)} />}
    </div>
  );
}

function useRefreshStock() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['products'] });
    void qc.invalidateQueries({ queryKey: ['movements'] });
  };
}

/** Stock take: type what is really on the shelf; the CRM books the difference. */
function StockCountDialog({ product, onClose }: { product: Product; onClose: () => void }) {
  const refresh = useRefreshStock();
  const current = Number(product.onHand ?? 0);
  const [counted, setCounted] = useState(String(current));
  const [note, setNote] = useState('');
  const diff = counted.trim() === '' ? null : Math.round((Number(counted) - current) * 1000) / 1000;
  const save = useMutation({
    mutationFn: () => api.post<{ difference: number }>('/inventory/count', { productId: product.id, countedQuantity: Number(counted), note: note || null }),
    onSuccess: (r) => {
      refresh();
      toast.success(r.difference === 0 ? 'המלאי תואם, לא נדרש תיקון' : `המלאי עודכן (${r.difference > 0 ? '+' : ''}${r.difference})`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="ספירת מלאי" description={`${product.name} · לפי המערכת: ${current} ${UNIT_LABELS[product.unit]}`}>
        <form onSubmit={(e) => { e.preventDefault(); if (diff !== null && Number(counted) >= 0) save.mutate(); }} className="grid gap-4">
          <Field label={`כמות בפועל (${UNIT_LABELS[product.unit]})`} hint={diff === null || diff === 0 ? 'אם הכמות זהה, לא יירשם תיקון' : `תירשם התאמה של ${diff > 0 ? '+' : ''}${diff}`}>
            {(id) => <Input id={id} type="number" min={0} step="0.001" value={counted} onChange={(e) => setCounted(e.target.value)} autoFocus />}
          </Field>
          <Field label="הערה">{(id) => <Input id={id} value={note} onChange={(e) => setNote(e.target.value)} placeholder="למשל: ספירה חודשית" />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={onClose}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>עדכון מלאי</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Fix a mistyped quantity: the wrong movement is cancelled and the right one booked, keeping the history. */
function FixMovementDialog({ movement, onClose }: { movement: Movement; onClose: () => void }) {
  const refresh = useRefreshStock();
  const isAdjustment = movement.movementType === 'adjustment';
  const [qty, setQty] = useState(String(isAdjustment ? movement.quantity : Math.abs(movement.quantity)));
  const [note, setNote] = useState(movement.note ?? '');
  const save = useMutation({
    mutationFn: () => api.patch(`/inventory/movements/${movement.id}`, { quantity: Number(qty), note: note || null }),
    onSuccess: () => {
      refresh();
      toast.success('התנועה תוקנה');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="תיקון תנועת מלאי"
        description={`${MOVEMENT_TYPE_LABELS[movement.movementType]} · ${movement.productName} · נרשם ${movement.quantity > 0 ? '+' : ''}${movement.quantity}. התנועה השגויה תסומן כמבוטלת ותירשם הכמות הנכונה.`}
      >
        <form onSubmit={(e) => { e.preventDefault(); if (Number(qty)) save.mutate(); }} className="grid gap-4">
          <Field label="הכמות הנכונה" hint={isAdjustment ? 'חיובי להוספה, שלילי להפחתה' : undefined}>
            {(id) => <Input id={id} type="number" step="0.001" value={qty} onChange={(e) => setQty(e.target.value)} autoFocus />}
          </Field>
          <Field label="הערה">{(id) => <Input id={id} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={onClose}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>שמירת תיקון</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CancelMovementDialog({ movement, onClose }: { movement: Movement; onClose: () => void }) {
  const refresh = useRefreshStock();
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => api.post(`/inventory/movements/${movement.id}/cancel`, { reason: reason || null }),
    onSuccess: () => {
      refresh();
      toast.success('התנועה בוטלה והמלאי עודכן');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const effect = -movement.quantity;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="ביטול תנועת מלאי"
        description={`${MOVEMENT_TYPE_LABELS[movement.movementType]} · ${movement.productName} · ${movement.quantity > 0 ? '+' : ''}${movement.quantity}. המלאי ישתנה ב-${effect > 0 ? '+' : ''}${effect}. התנועה תישאר בהיסטוריה מסומנת כמבוטלת.`}
      >
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="grid gap-4">
          <Field label="סיבה (לא חובה)">{(id) => <Input id={id} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="למשל: הוזן בטעות" />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={onClose}>חזרה</Button>
            <Button type="submit" variant="danger" loading={save.isPending}>ביטול התנועה</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProductDialog({ product, open, onOpenChange }: { product: Product | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ sku: '', name: '', unit: 'kg' as ProductUnit, listPrice: '', cost: '', minStock: '', trackInventory: true, isActive: true, description: '' });
  const [tiers, setTiers] = useState<{ minQty: string; unitPrice: string }[]>([]);
  useEffect(() => {
    if (!open) return;
    setF({
      sku: product?.sku ?? '', name: product?.name ?? '', unit: product?.unit ?? 'kg', listPrice: product ? String(product.listPrice) : '',
      cost: product?.cost != null ? String(product.cost) : '', minStock: product?.minStock != null ? String(product.minStock) : '',
      trackInventory: product?.trackInventory ?? true, isActive: product?.isActive ?? true, description: product?.description ?? '',
    });
    setTiers(product?.tiers.map((t) => ({ minQty: String(t.minQty), unitPrice: String(t.unitPrice) })) ?? []);
  }, [open, product]);
  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...f,
        listPrice: Number(f.listPrice),
        cost: f.cost === '' ? null : Number(f.cost),
        minStock: f.minStock === '' ? null : Number(f.minStock),
        description: f.description || null,
        tiers: tiers.filter((t) => t.minQty && t.unitPrice).map((t) => ({ minQty: Number(t.minQty), unitPrice: Number(t.unitPrice) })),
      };
      return product ? api.patch(`/products/${product.id}`, body) : api.post('/products', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      toast.success('המוצר נשמר');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={product ? 'עריכת מוצר' : 'מוצר חדש'} wide>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Field label="שם המוצר" required className="sm:col-span-2">{(id) => <Input id={id} value={f.name} onChange={set('name')} />}</Field>
          <Field label="מק״ט" required>{(id) => <Input id={id} dir="ltr" className="text-start" value={f.sku} onChange={set('sku')} placeholder="TKM-CER-1KG" />}</Field>
          <Field label="יחידה">
            {(id) => (
              <Select id={id} value={f.unit} onChange={set('unit')}>
                {PRODUCT_UNITS.map((u) => <option key={u} value={u}>{UNIT_LABELS[u]}</option>)}
              </Select>
            )}
          </Field>
          <Field label="מחירון ליחידה (₪, לפני מע״מ)" required>{(id) => <Input id={id} type="number" min={0} step="0.01" value={f.listPrice} onChange={set('listPrice')} />}</Field>
          <Field label="עלות ליחידה (₪)">{(id) => <Input id={id} type="number" min={0} step="0.01" value={f.cost} onChange={set('cost')} />}</Field>
          <Field label="מלאי מינימום (התראה)">{(id) => <Input id={id} type="number" min={0} step="0.001" value={f.minStock} onChange={set('minStock')} />}</Field>
          <div className="flex flex-col justify-end gap-2 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" className="size-4 accent-matcha-700" checked={f.trackInventory} onChange={(e) => setF((x) => ({ ...x, trackInventory: e.target.checked }))} /> מעקב מלאי</label>
            <label className="flex items-center gap-2"><input type="checkbox" className="size-4 accent-matcha-700" checked={f.isActive} onChange={(e) => setF((x) => ({ ...x, isActive: e.target.checked }))} /> פעיל</label>
          </div>
          <div className="rounded-card border border-line bg-sunken/40 p-3 sm:col-span-2">
            <div className="mb-2 flex items-center justify-between text-sm font-semibold">
              מדרגות מחיר לפי כמות
              <Button size="sm" variant="ghost" onClick={() => setTiers((t) => [...t, { minQty: '', unitPrice: '' }])}><Plus /> מדרגה</Button>
            </div>
            {tiers.map((t, i) => (
              <div key={i} className="mb-2 flex items-center gap-2">
                <span className="text-xs text-muted">מכמות</span>
                <Input type="number" min={0} step="0.001" className="w-28" aria-label="מכמות" value={t.minQty} onChange={(e) => setTiers((ts) => ts.map((x, j) => (j === i ? { ...x, minQty: e.target.value } : x)))} />
                <span className="text-xs text-muted">מחיר</span>
                <Input type="number" min={0} step="0.01" className="w-32" aria-label="מחיר מדרגה" value={t.unitPrice} onChange={(e) => setTiers((ts) => ts.map((x, j) => (j === i ? { ...x, unitPrice: e.target.value } : x)))} />
                <Button size="icon-sm" variant="ghost" aria-label="הסרת מדרגה" onClick={() => setTiers((ts) => ts.filter((_, j) => j !== i))}><Trash2 /></Button>
              </div>
            ))}
            {!tiers.length && <p className="text-xs text-muted">ללא מדרגות — המחיר הוא מחיר המחירון לכל כמות.</p>}
          </div>
          <Field label="תיאור" className="sm:col-span-2">{(id) => <Textarea id={id} rows={2} value={f.description} onChange={set('description')} />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>שמירה</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MovementDialog({ product, open, onOpenChange }: { product: Product; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [type, setType] = useState<'purchase' | 'adjustment' | 'damage' | 'return'>('purchase');
  const [qty, setQty] = useState('');
  const [batchCode, setBatchCode] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const suppliers = useSuppliers();
  const save = useMutation({
    mutationFn: () => api.post('/inventory/movements', { productId: product.id, movementType: type, quantity: Number(qty), batchCode: batchCode || null, supplierId: supplierId || null, expiresAt: expiresAt || null, note: note || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['movements'] });
      toast.success('תנועת המלאי נרשמה');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="תנועת מלאי" description={`${product.name} · במלאי ${Number(product.onHand ?? 0)} ${UNIT_LABELS[product.unit]}`}>
        <form onSubmit={(e) => { e.preventDefault(); if (Number(qty)) save.mutate(); }} className="grid gap-4 sm:grid-cols-2">
          <Field label="סוג">
            {(id) => (
              <Select id={id} value={type} onChange={(e) => setType(e.target.value as typeof type)}>
                {(['purchase', 'adjustment', 'damage', 'return'] as const).map((t) => <option key={t} value={t}>{MOVEMENT_TYPE_LABELS[t]}</option>)}
              </Select>
            )}
          </Field>
          <Field label="כמות" hint={type === 'adjustment' ? 'חיובי להוספה, שלילי להפחתה' : undefined}>{(id) => <Input id={id} type="number" step="0.001" value={qty} onChange={(e) => setQty(e.target.value)} />}</Field>
          {type === 'purchase' && (
            <>
              <Field label="קוד אצווה">{(id) => <Input id={id} dir="ltr" value={batchCode} onChange={(e) => setBatchCode(e.target.value)} />}</Field>
              <Field label="תוקף">{(id) => <Input id={id} type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />}</Field>
              <Field label="ספק" className="sm:col-span-2">
                {(id) => (
                  <Select id={id} value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                    <option value="">—</option>
                    {suppliers.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </Select>
                )}
              </Field>
            </>
          )}
          <Field label="הערה" className="sm:col-span-2">{(id) => <Input id={id} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>רישום</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
